import { FilterOutlined, LoadingOutlined } from '@ant-design/icons';
import { useAuth, useProducts } from '@hooks';
import {
  Button,
  Checkbox,
  Col,
  Drawer,
  Empty,
  Form,
  Input,
  message,
  Modal,
  Row,
  Select,
  Space,
  Spin,
  Typography
} from 'antd';
import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';


const { Title, Paragraph } = Typography;
const { TextArea } = Input;
const { Option } = Select;


interface ContactFormValues {
  name: string;
  email: string;
  phone: string;
  subject: string;
  message: string;
}

interface ContactFormProps {
  onFinish: (values: ContactFormValues) => void;
  isSubmitting: boolean;
  products: any[];
}

// --- Componente do Formulário de Contato ---
const ContactForm = ({ onFinish, isSubmitting, products }: ContactFormProps) => {
  return (
    <Form
      name="contact-form-modal"
      layout="vertical"
      onFinish={onFinish}
      requiredMark={false}
      disabled={isSubmitting}
    >
      <Form.Item
        label={<span className="font-android text-white">FULL NAME</span>}
        name="name"
        rules={[{ required: true, message: 'please enter your name' }]}
      >
        <Input className="!bg-zinc-700 font-barlow border-zinc-600 text-white !rounded-none" placeholder="Your full name" />
      </Form.Item>
      <Form.Item
        label={<span className="font-android text-white">EMAIL ADDRESS</span>}
        name="email"
        rules={[{ required: true, message: 'please enter your email' }, { type: 'email' }]}
      >
        <Input className="!bg-zinc-700 font-barlow border-zinc-600 text-white !rounded-none" placeholder="Your email address" />
      </Form.Item>
      <Form.Item
        label={<span className="font-android text-white">PHONE NUMBER</span>}
        name="phone"
        rules={[{ required: true, message: 'please enter your phone number' }]}
      >
        <Input className="!bg-zinc-700 font-barlow border-zinc-600 text-white !rounded-none" placeholder="Your phone number" />
      </Form.Item>
      <Form.Item
        label={<span className="font-android text-white">PRODUCT INTERESTED IN</span>}
        name="subject"
        rules={[{ required: true, message: 'please select a product' }]}
      >
        <Select placeholder="Select a product or category" rootClassName="ant-select-dark" className="!rounded-none">
          {products.map((p: any) => <Option key={p.id} value={p.name}>{p.name}</Option>)}
          <Option value="Other">Other</Option>
        </Select>
      </Form.Item>
      <Form.Item
        label={<span className="font-android text-white">MESSAGE</span>}
        name="message"
        rules={[{ required: true, message: 'please enter your message' }]}
      >
        <TextArea rows={4} className="!bg-zinc-700 font-barlow text-white !rounded-none" placeholder="Your message" />
      </Form.Item>
      <div className="text-center pt-4">
        <button type="submit" className="bg-yellow-principal text-black font-android text-xl tracking-wider w-55 h-10 cursor-pointer hover:opacity-90 transition-opacity" disabled={isSubmitting}>
          {isSubmitting ? 'SENDING...' : 'SEND MESSAGE'}
        </button>
      </div>
    </Form>
  );
};

// --- Props para o Componente de Filtro ---
interface FilterContentProps {
  selectedCategories: string[];
  onCategoryChange: (checkedValues: (string | number | boolean)[]) => void;
  availableCategories: string[];
}

// --- Componente de Filtro Atualizado ---
const FilterContent = ({ selectedCategories, onCategoryChange, availableCategories }: FilterContentProps) => (
  <div className="space-y-8">
    {/* Filtro por Categoria */}
    <div>
      <Title level={5} className="font-barlow text-white uppercase border-b border-zinc-700 pb-2 mb-4">Product Category</Title>
      {availableCategories && availableCategories.length > 0 ? (
        <Checkbox.Group
          className="w-full"
          value={selectedCategories}
          onChange={onCategoryChange}
        >
          <Space direction="vertical">
            {availableCategories.map(category => (
              <Checkbox key={category} value={category} className="font-android text-white filter-checkbox">
                {category}
              </Checkbox>
            ))}
          </Space>
        </Checkbox.Group>
      ) : (
        <Paragraph className="font-barlow text-white">No categories available</Paragraph>
      )}
    </div>
  </div>
);


export const MerchPage = () => {
  const navigate = useNavigate();
  const [isModalVisible, setIsModalVisible] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isFilterDrawerVisible, setIsFilterDrawerVisible] = useState(false);

  // Fetch products from API
  const { products } = useProducts();
  const { profile: { data: profile } } = useAuth();
  const isAuthenticated = !!profile;

  // --- Estado para os filtros selecionados ---
  const [selectedCategories, setSelectedCategories] = useState<string[]>([]);

  const showModal = () => setIsModalVisible(true);
  const handleCancel = () => setIsModalVisible(false);

  // --- Handler para atualizar o estado dos filtros ---
  const handleFilterChange = (checkedValues: (string | number | boolean)[]) => {
    setSelectedCategories(checkedValues as string[]);
  };

  const onFormFinish = async (values: ContactFormValues) => {
    setIsSubmitting(true);
    console.log('Form values:', values);
    await new Promise(resolve => setTimeout(resolve, 1500));
    setIsSubmitting(false);
    setIsModalVisible(false);
    message.success('Your request has been sent successfully!');
  };

  const handleProductClick = (productId: string) => {
    navigate(`/merch/${productId}`);
  };


  // Helper function to get product image URL
  const getProductImage = (product: any) => {
    const bucketName = import.meta.env.VITE_AWS_S3_BUCKET || 'ruppert-production';
    const region = import.meta.env.VITE_AWS_S3_BUCKET_REGION || 'us-east-2';

    // Use images array if available (first image)
    if (product.images && product.images.length > 0) {
      const firstImage = product.images[0];
      if (firstImage.startsWith('http')) {
        return firstImage;
      }
      return `https://${bucketName}.s3.${region}.amazonaws.com/${firstImage}`;
    }

    // Fallback to legacy imageUrl
    if (product.imageUrl) {
      if (product.imageUrl.startsWith('http')) {
        return product.imageUrl;
      }
      return `https://${bucketName}.s3.${region}.amazonaws.com/${product.imageUrl}`;
    }

    return `https://placehold.co/400x400/181717/E3AF25?text=${encodeURIComponent(product.name)}`;
  };

  // Format price
  const formatPrice = (price: number | string) => {
    const numPrice = typeof price === 'string' ? parseFloat(price) : price;
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD',
    }).format(numPrice);
  };

  // Truncate description to max characters
  const truncateDescription = (description: string, maxLength: number = 120) => {
    if (!description) return '';
    if (description.length <= maxLength) return description;
    return description.substring(0, maxLength).trim() + '...';
  };

  // Get unique categories from products
  const availableCategories = React.useMemo(() => {
    if (!products.data || products.data.length === 0) {
      return [];
    }

    const categories = products.data
      .map((product: any) => product.category)
      .filter((category: string | null | undefined) => category != null && category !== '');

    // Return unique categories
    return Array.from(new Set(categories)).sort();
  }, [products.data]);

  // Filter products based on selected categories
  const displayProducts = React.useMemo(() => {
    if (!products.data || products.data.length === 0) {
      return [];
    }

    let filtered = products.data;

    // Apply category filter if any categories are selected
    if (selectedCategories.length > 0) {
      filtered = filtered.filter((product: any) =>
        product.category && selectedCategories.includes(product.category)
      );
    }

    return filtered;
  }, [products.data, selectedCategories]);

  return (
    <>
      <div className="overflow-x-hidden">

        {/* 1. Hero Section */}
        <section className="relative w-full h-[400px]">
          <div className="merch-bg opacity-60 absolute inset-0 w-full h-full"></div>
          <div className="container relative h-full flex flex-col justify-center items-center mx-auto px-4 py-12 md:py-16 lg:py-20 z-10">
            <Title level={1} className="font-android !font-normal text-yellow-principal text-center">
              OUR PRODUCTS
            </Title>
            <div className="max-w-3xl mx-auto">
              <Paragraph className="font-barlow text-gray-300 text-center md:text-lg lg:!text-xl">
                Innovative traction solutions engineered for the toughest winter conditions
              </Paragraph>
            </div>
          </div>
        </section>

        {/* 2. Categorias - Ocultada */}
        {/* <section className="py-16 bg-black">
          <div className="container mx-auto px-4">
            <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
              <div className="relative">
                <div
                  className="relative bg-zinc-800 border-4 border-white p-8 text-center flex flex-col items-center justify-center"
                  style={{
                    clipPath: 'polygon(-20px 0, 100% 0, 100% 100%, 0 100%, 0 -20px)',
                    minHeight: '280px'
                  }}
                >
                  <div
                    className="pointer-events-none absolute top-0 left-0"
                    style={{ width: 20, height: 20, backgroundColor: 'white', clipPath: 'polygon(0 0, 100% 0, 0 100%)' }}
                  />
                  <div
                    className="pointer-events-none absolute bottom-0 right-0"
                    style={{ width: 20, height: 20, backgroundColor: 'white', clipPath: 'polygon(100% 0, 100% 100%, 0 100%)' }}
                  />
                  <Title level={2} className="font-android !font-normal text-yellow-principal uppercase">Revo Hook</Title>
                  <Paragraph className="font-barlow text-gray-300">Revolutionary hook system for quick and secure chain installation</Paragraph>
                </div>
              </div>
              <div className="relative">
                <div
                  className="relative bg-zinc-800 border-4 border-white p-8 text-center flex flex-col items-center justify-center"
                  style={{
                    clipPath: 'polygon(-20px 0, 100% 0, 100% 100%, 0 100%, 0 -20px)',
                    minHeight: '280px'
                  }}
                >
                  <div
                    className="pointer-events-none absolute top-0 left-0"
                    style={{ width: 20, height: 20, backgroundColor: 'white', clipPath: 'polygon(0 0, 100% 0, 0 100%)' }}
                  />
                  <div
                    className="pointer-events-none absolute bottom-0 right-0"
                    style={{ width: 20, height: 20, backgroundColor: 'white', clipPath: 'polygon(100% 0, 100% 100%, 0 100%)' }}
                  />
                  <Title level={2} className="font-android !font-normal text-yellow-principal uppercase">Hub System</Title>
                  <Paragraph className="font-barlow text-gray-300">Advanced hub technology for optimal traction and performance</Paragraph>
                </div>
              </div>
              <div className="relative">
                <div
                  className="relative bg-zinc-800 border-4 border-white p-8 text-center flex flex-col items-center justify-center"
                  style={{
                    clipPath: 'polygon(-20px 0, 100% 0, 100% 100%, 0 100%, 0 -20px)',
                    minHeight: '280px'
                  }}
                >
                  <div
                    className="pointer-events-none absolute top-0 left-0"
                    style={{ width: 20, height: 20, backgroundColor: 'white', clipPath: 'polygon(0 0, 100% 0, 0 100%)' }}
                  />
                  <div
                    className="pointer-events-none absolute bottom-0 right-0"
                    style={{ width: 20, height: 20, backgroundColor: 'white', clipPath: 'polygon(100% 0, 100% 100%, 0 100%)' }}
                  />
                  <Title level={2} className="font-android !font-normal text-yellow-principal uppercase">Veriga Chains</Title>
                  <Paragraph className="font-barlow text-gray-300">Premium quality snow chains for trucks, skid steers and heavy equipment</Paragraph>
                </div>
              </div>
            </div>
          </div>
        </section> */}

        {/* 3. Filtros e Grade de Produtos */}
        <main className="bg-[url(/assets/images/Background.png)] bg-center bg-cover">
          <div className="container mx-auto px-4 py-24">
            <div className="lg:hidden text-center mb-8">
              <Button
                type="primary"
                className="bg-yellow-principal text-black font-android tracking-wider hover:opacity-90"
                icon={<FilterOutlined />}
                size="large"
                onClick={() => setIsFilterDrawerVisible(true)}
              >
                FILTERS
              </Button>
            </div>

            <Row gutter={[48, 48]}>
              {/* Coluna de Filtros Desktop: agora passa as props */}
              <Col className="hidden lg:block" lg={6}>
                <Title level={2} className="font-android !font-normal text-yellow-principal uppercase mb-6">Filters</Title>
                <FilterContent
                  selectedCategories={selectedCategories}
                  onCategoryChange={handleFilterChange}
                  availableCategories={availableCategories}
                />
              </Col>

              {/* Coluna de Produtos: agora usa filteredProducts */}
              <Col xs={24} lg={18}>
                {products.isLoading ? (
                  <div className="flex justify-center items-center py-20">
                    <Spin indicator={<LoadingOutlined style={{ fontSize: 48, color: '#E3AF25' }} spin />} />
                  </div>
                ) : products.isError ? (
                  <div className="text-center py-20">
                    <Empty
                      description={
                        <span className="font-barlow text-white">
                          Error loading products. Please try again later.
                        </span>
                      }
                    />
                    <Button
                      type="primary"
                      className="bg-yellow-principal text-black font-android mt-4 hover:opacity-90"
                      onClick={() => products.refetch()}
                    >
                      TRY AGAIN
                    </Button>
                  </div>
                ) : displayProducts.length === 0 ? (
                  <div className="text-center py-20">
                    <Empty
                      description={
                        <span className="font-barlow text-white">
                          No products available at the moment.
                        </span>
                      }
                    />
                  </div>
                ) : (
                  <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-8">
                    {displayProducts.map((product: any) => (
                      <div key={product.id} className="bg-zinc-900 overflow-hidden group flex flex-col max-h-[600px]">
                        <div
                          onClick={() => handleProductClick(product.id)}
                          className="block cursor-pointer product-card-hover flex-1 flex flex-col"
                        >
                          <img
                            src={getProductImage(product)}
                            alt={product.name}
                            className="w-full h-64 object-cover product-image transition-all duration-300 flex-shrink-0"
                            onError={(e) => {
                              e.currentTarget.src = `https://placehold.co/400x400/181717/E3AF25?text=${encodeURIComponent(product.name)}`;
                            }}
                          />
                          <div className="p-6 flex-1 flex flex-col">
                            <Title level={4} className="font-android !font-normal text-yellow-principal uppercase flex-shrink-0">{product.name}</Title>
                            {isAuthenticated && product.variants && product.variants.length > 0 && (
                              <div className="font-android text-yellow-principal font-normal text-xl mb-2 tracking-wider flex-shrink-0">
                                {(() => {
                                  const prices = product.variants.map((v: any) => v.price);
                                  const minPrice = Math.min(...prices);
                                  const maxPrice = Math.max(...prices);
                                  if (minPrice === maxPrice) {
                                    return formatPrice(minPrice);
                                  }
                                  return `${formatPrice(minPrice)} - ${formatPrice(maxPrice)}`;
                                })()}
                              </div>
                            )}
                            <Paragraph className="font-barlow text-white flex-1 overflow-hidden">
                              {truncateDescription(product.description)}
                            </Paragraph>
                          </div>
                        </div>
                        <div className="p-6 pt-0 flex-shrink-0">
                          <button
                            onClick={() => handleProductClick(product.id)}
                            className="bg-yellow-principal text-black font-android font-bold text-xl w-full h-10 tracking-wider cursor-pointer hover-3d uppercase"
                          >
                            VIEW DETAILS
                          </button>
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </Col>
            </Row>
          </div>
        </main>

        {/* 4. Seção de Vídeo - Ocultada */}
        {/* <section className="py-16 bg-black pb-24">
          <div className="container mx-auto px-4 text-center">
            <Title level={1} className="font-android !font-normal text-yellow-principal uppercase">See It In Action</Title>
            <Paragraph className="font-barlow text-white !text-xl tracking-wider max-w-3xl mx-auto pb-12">
              Watch how the Ruppert system revolutionizes chain installation, saving time and hassle on the job site.
            </Paragraph>
            <div className="w-full max-w-5xl mx-auto bg-zinc-900 aspect-video">
              <iframe
                src="https://www.youtube.com/embed/pR5xzGbEpBA"
                title="Product Video"
                allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
                allowFullScreen
                className="w-full h-full"
              ></iframe>
            </div>
          </div>
        </section> */}

        {/* 5. Seção Final "Need a Quote?" */}
        <section className="bg-black py-20">
          <div className="container mx-auto px-4 text-center">
            <Title level={1} className="font-android !font-normal text-white uppercase pb-4">Need a Quote?</Title>
            <Paragraph className="font-barlow text-white max-w-2xl tracking-wider mx-auto mt-4 pb-8 text-base md:!text-xl">
              Let us know your requirements, and our team will help you assemble the perfect package for your needs.
            </Paragraph>
            <button onClick={showModal} className="bg-yellow-principal text-black font-android font-semibold !text-xl w-full max-w-md sm:w-96 py-4 tracking-wider cursor-pointer hover-3d uppercase">
              Request an Estimate
            </button>
          </div>
        </section>

        {/* Modal com Formulário de Contato */}
        <Modal
          title={<Title level={3} className="font-android !font-normal text-yellow-principal">REQUEST INFORMATION</Title>}
          open={isModalVisible}
          onCancel={handleCancel}
          footer={null}
          closeIcon={<span className="text-yellow-principal !pt-2.5">X</span>}
          centered
          className="contact-form-modal"
          styles={{
            mask: { backdropFilter: 'blur(10px)', backgroundColor: 'rgba(0,0,0,0.7)' },
            content: { backgroundColor: '#181717', border: '1px solid #3f3f46', borderRadius: '0px', width: 'auto', maxWidth: '500px' },
            header: { backgroundColor: 'transparent', borderBottom: 'none' },
          }}
        >
          <style>{`
            .contact-form-modal .ant-select-selector {
              border-radius: 0 !important;
            }
            .contact-form-modal .ant-select {
              border-radius: 0 !important;
            }
            .contact-form-modal .ant-select-arrow {
              color: white !important;
            }
            .contact-form-modal .ant-select-arrow svg {
              color: white !important;
              fill: white !important;
            }
          `}</style>
          <ContactForm onFinish={onFormFinish} isSubmitting={isSubmitting} products={displayProducts} />
        </Modal>

        {/* Drawer de Filtros Mobile: agora passa as props */}
        <Drawer
          placement="left"
          onClose={() => setIsFilterDrawerVisible(false)}
          open={isFilterDrawerVisible}
          closeIcon={<span className="text-yellow-principal text-2xl">X</span>}
          styles={{
            header: { backgroundColor: '#181717', borderBottom: '1px solid #3f3f46' },
            body: { backgroundColor: '#181717', padding: '24px' },
          }}
        >
          <FilterContent
            selectedCategories={selectedCategories}
            onCategoryChange={handleFilterChange}
            availableCategories={availableCategories}
          />
        </Drawer>
      </div>
    </>
  );
};

